import { useState, useEffect, useMemo } from "react";
import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, PackageSearch, MapPin, LayoutGrid,
  Tag, Users, Star, Gavel, BarChart2, Building2, ExternalLink,
  Menu, X, ChevronLeft, ChevronRight, BadgeCheck, Truck, Navigation,
  Armchair, Wallet, Printer,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { getImageUrl } from "../../utils/getImageUrl";
import { isFoodCategory } from "../../utils/isRestaurantCategory";

const NAV = [
  { label: "Dashboard",  icon: LayoutDashboard, to: "/company",          end: true, module: "dashboard" },
  { label: "Pedidos",    icon: ShoppingBag,     to: "/company/orders",   badge: true, module: "pedidos" },
  { label: "Productos",  icon: PackageSearch,   to: "/company/posts",    module: "productos" },
  { label: "Sedes",      icon: MapPin,          to: "/company/sedes",    module: "sedes" },
  { label: "Secciones",  icon: LayoutGrid,      to: "/company/seccions", module: "secciones" },
  { label: "Cupones",    icon: Tag,             to: "/company/coupons",  module: "cupones" },
  { label: "Ryders",     icon: Truck,           to: "/company/ryders",  module: "ryders" },
  { label: "Despacho",   icon: Navigation,      to: "/company/dispatch", module: "despacho" },
  { label: "Reseñas",    icon: Star,            to: "/company/reviews", module: "resenas" },
  { label: "Subastas",   icon: Gavel,           to: "/company/auctions", module: "subastas" },
  { label: "Usuarios",   icon: Users,           to: "/company/users",   module: "usuarios" },
  { label: "Métricas",   icon: BarChart2,       to: "/company/metrics", module: "metricas" },
];

const MESAS_ITEM = { label: "Mesas", icon: Armchair, to: "/company/mesas", module: "mesas" };
const CAJA_ITEM = { label: "Caja del día", icon: Wallet, to: "/company/caja", module: "caja" };
const IMPRESORAS_ITEM = { label: "Impresoras", icon: Printer, to: "/company/impresoras", module: "impresoras" };

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

export default function CompanyLayout() {
  // All hooks must be called unconditionally — before any early return
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [collapsed, setCollapsed]       = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [company, setCompany]           = useState(() => getStoredUser()?.company ?? null);
  const [allowedModules, setAllowedModules] = useState(null); // null = acceso completo
  // Módulos de la plataforma que activa la categoría de la empresa (lo configura el super
  // admin). null = la empresa no tiene categoría y se usa la heurística de restaurante.
  const [categoryFeatures, setCategoryFeatures] = useState(null);
  const location = useLocation();

  const stored    = getStoredUser();
  const isAuth    = Boolean(stored && stored.role === "company");
  const companyId = company?.id ?? stored?.companyId;
  const logoSrc   = company?.logo
    ? getImageUrl(company.logo)
    : null;

  const navItems = useMemo(() => {
    let items = NAV;
    const pedidosIdx = NAV.findIndex((n) => n.to === "/company/orders");
    if (categoryFeatures) {
      const extra = [];
      if (categoryFeatures.includes("mesas")) extra.push(MESAS_ITEM, CAJA_ITEM);
      if (categoryFeatures.includes("impresoras")) extra.push(IMPRESORAS_ITEM);
      if (extra.length) items = [...NAV.slice(0, pedidosIdx + 1), ...extra, ...NAV.slice(pedidosIdx + 1)];
    } else if (isFoodCategory(company?.category)) {
      items = [
        ...NAV.slice(0, pedidosIdx + 1),
        MESAS_ITEM, CAJA_ITEM, IMPRESORAS_ITEM,
        ...NAV.slice(pedidosIdx + 1),
      ];
    }
    if (allowedModules === null) return items;
    return items.filter((n) => allowedModules.includes(n.module));
  }, [company, allowedModules, categoryFeatures]);

  // Permisos por módulo (rol de empresa) — se resuelve siempre al montar, sin
  // depender de si `company` ya estaba en localStorage, porque el rol/módulos
  // permitidos pueden haber cambiado desde la última sesión.
  useEffect(() => {
    if (!isAuth) return;
    API.get("/auth/me")
      .then((r) => {
        const user = r.data?.data?.user ?? r.data?.user;
        if (!user) return;
        const isOwner = user.company?.ownerId && user.id === user.company.ownerId;
        setCategoryFeatures(user.company?.category ? (user.company.category.features ?? []) : null);
        if (isOwner || !user.companyRoleId) {
          setAllowedModules(null);
        } else {
          setAllowedModules(user.companyRole?.modules ?? []);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [isAuth]);

  // Load company info once if not already in localStorage
  useEffect(() => {
    if (!isAuth || company) return;
    API.get("/auth/me")
      .then(r => {
        const user = r.data?.data?.user ?? r.data?.user;
        const cid  = user?.companyId ?? user?.company?.id;
        if (!cid) return;
        return API.get(`/companies/by-id/${cid}`);
      })
      .then(r => {
        if (!r) return;
        const comp = r.data?.data?.company ?? r.data?.data;
        if (comp) {
          setCompany(comp);
          const u = getStoredUser();
          localStorage.setItem("user", JSON.stringify({ ...u, company: comp, companyId: comp.id }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line
  }, [isAuth]);

  // Poll pending orders count
  useEffect(() => {
    if (!companyId) return;
    const poll = () =>
      API.get(`/orders/company/${companyId}/pending-count`)
        .then(r => setPendingCount(r.data?.data?.count ?? r.data?.count ?? 0))
        .catch(() => {});
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, [companyId]);

  // Auth guard after all hooks
  if (!isAuth) return <Navigate to="/login" replace />;

  // Si el rol de empresa del usuario no incluye "dashboard", no debe caer ahí al
  // iniciar sesión — se manda directo a Mesas (si la tiene) o al primer módulo
  // permitido. Solo aplica en la ruta exacta /company (el Dashboard).
  if (location.pathname === "/company" && allowedModules !== null && !allowedModules.includes("dashboard")) {
    const fallback = allowedModules.includes("mesas")
      ? "/company/mesas"
      : navItems.find((n) => n.to !== "/company")?.to;
    if (fallback) return <Navigate to={fallback} replace />;
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-center px-6">
        <p className="text-sm text-gray-500">No tienes acceso a ningún módulo. Contacta al administrador de tu empresa.</p>
      </div>
    );
  }

  function SidebarInner({ onNav }) {
    return (
      <div className="flex flex-col h-full">
        {/* Company identity */}
        <div className={`flex items-center gap-3 px-3 py-4 border-b border-gray-100 min-h-[64px] ${collapsed ? "justify-center" : ""}`}>
          {logoSrc ? (
            <img src={logoSrc} alt={company?.name} className="w-9 h-9 rounded-xl object-cover border border-gray-200 shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-supernova-100 flex items-center justify-center shrink-0">
              <Building2 size={18} className="text-supernova-600" />
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{company?.name ?? "Mi tienda"}</p>
                {company?.verified && <BadgeCheck size={13} className="text-blue-500 shrink-0" />}
              </div>
              <p className="text-[10px] text-gray-400 leading-tight">Panel de empresa</p>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-2 px-2 space-y-0.5">
          {navItems.map(({ label, icon: Icon, to, badge, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onNav}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium transition-all group ${
                  isActive
                    ? "bg-supernova-50 text-supernova-700"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                } ${collapsed ? "justify-center" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={18} className={`shrink-0 ${isActive ? "text-supernova-600" : "text-gray-500 group-hover:text-gray-700"}`} />
                  {!collapsed && <span className="flex-1 truncate">{label}</span>}
                  {badge && pendingCount > 0 && (
                    <span className={`font-bold text-white bg-red-500 rounded-full flex items-center justify-center ${
                      collapsed
                        ? "absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px]"
                        : "min-w-[20px] h-5 px-1 text-[10px] shrink-0"
                    }`}>
                      {pendingCount > 99 ? "99+" : pendingCount}
                    </span>
                  )}
                  {/* Tooltip on collapsed */}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                      {label}
                    </div>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: public store link + collapse toggle */}
        <div className="p-2 border-t border-gray-100 space-y-0.5">
          {companyId && (
            <a
              href={`/company/${companyId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 px-2.5 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition group ${collapsed ? "justify-center" : ""}`}
            >
              <ExternalLink size={16} className="shrink-0" />
              {!collapsed && <span>Ver mi tienda</span>}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                  Ver mi tienda
                </div>
              )}
            </a>
          )}
          {/* Collapse button — desktop only */}
          <button
            onClick={() => setCollapsed(v => !v)}
            className="hidden lg:flex w-full items-center gap-3 px-2.5 py-2 rounded-xl text-xs font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
            style={{ justifyContent: collapsed ? "center" : "flex-start" }}
          >
            {collapsed ? <ChevronRight size={15} /> : <><ChevronLeft size={15} /><span>Colapsar</span></>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-50" style={{ minHeight: "calc(100vh - 56px)" }}>

      {/* ── Sidebar desktop ─────────────────────────────── */}
      <aside
        className={`hidden lg:block shrink-0 bg-white border-r border-gray-200 transition-all duration-200`}
        style={{ width: collapsed ? 64 : 224 }}
      >
        <div className="sticky top-14 overflow-y-auto" style={{ height: "calc(100vh - 56px)" }}>
          <SidebarInner onNav={() => {}} />
        </div>
      </aside>

      {/* ── Mobile drawer backdrop ───────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Mobile drawer ────────────────────────────────── */}
      <aside className={`fixed top-0 left-0 bottom-0 z-50 w-64 bg-white border-r border-gray-200 shadow-xl flex flex-col lg:hidden transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-gray-100 shrink-0">
          <span className="font-bold text-gray-800 text-sm">Menú</span>
          <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          <SidebarInner onNav={() => setMobileOpen(false)} />
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 px-4 h-12 bg-white border-b border-gray-200 shrink-0 sticky top-14 z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-1.5 -ml-1 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <Menu size={20} />
          </button>
          {logoSrc ? (
            <img src={logoSrc} alt={company?.name} className="w-7 h-7 rounded-lg object-cover shrink-0" />
          ) : (
            <Building2 size={17} className="text-supernova-600 shrink-0" />
          )}
          <span className="font-bold text-gray-800 text-sm truncate">{company?.name ?? "Mi tienda"}</span>
          {pendingCount > 0 && (
            <span className="ml-auto flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
              <ShoppingBag size={10} /> {pendingCount}
            </span>
          )}
        </div>

        {/* Routed page */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
